import * as React from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useKeyPress } from "react-use";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import {
  Measurement,
  useMeasuringToolState,
} from "../../containers/MeasuringToolContainer";
import { formatNumber } from "../../utils";
import { feetToMeters } from "../../utils/math";
import { ClearCanvasOverlay } from "../ClearCanvasOverlay/ClearCanvasOverlay";

// MUI primary blue for selection
const SELECTED_COLOR = "#1976d2";
const DEFAULT_COLOR = "black";

/**
 * Calculate the perpendicular distance from a point to a line segment
 */
const pointToLineDistance = (
  point: { x: number; y: number },
  lineStart: { x: number; y: number },
  lineEnd: { x: number; y: number }
): number => {
  const A = point.x - lineStart.x;
  const B = point.y - lineStart.y;
  const C = lineEnd.x - lineStart.x;
  const D = lineEnd.y - lineStart.y;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  const param = lenSq !== 0 ? dot / lenSq : -1;

  let xx: number, yy: number;
  if (param < 0) {
    xx = lineStart.x;
    yy = lineStart.y;
  } else if (param > 1) {
    xx = lineEnd.x;
    yy = lineEnd.y;
  } else {
    xx = lineStart.x + param * C;
    yy = lineStart.y + param * D;
  }

  return Math.sqrt((point.x - xx) ** 2 + (point.y - yy) ** 2);
};

/**
 * Hit test to find if a click is near any existing measurement
 * Measurements are stored in feet, so we convert to pixels for comparison
 */
const hitTestMeasurement = (
  clickX: number,
  clickY: number,
  measurements: Measurement[],
  pixelsToFeetRatio: number,
  zoomLevel: number = 1,
  baseScale: number = 1
): string | null => {
  // Guard against division by zero
  if (pixelsToFeetRatio <= 0) return null;

  // Scale tolerance with zoom (clamped to same range as drawing)
  const clampedZoom = Math.max(0.5, Math.min(1, zoomLevel)) * baseScale;
  const CLICK_TOLERANCE = 12 * clampedZoom; // pixels, scaled with zoom

  for (const measurement of measurements) {
    // Convert measurement coordinates from feet to current pixels
    const startPixels = {
      x: measurement.startPointFeet.x * pixelsToFeetRatio,
      y: measurement.startPointFeet.y * pixelsToFeetRatio,
    };
    const endPixels = {
      x: measurement.endPointFeet.x * pixelsToFeetRatio,
      y: measurement.endPointFeet.y * pixelsToFeetRatio,
    };

    const distance = pointToLineDistance(
      { x: clickX, y: clickY },
      startPixels,
      endPixels
    );
    if (distance < CLICK_TOLERANCE) {
      return measurement.id;
    }
  }
  return null;
};

/**
 * Apply shift constraint to make line horizontal or vertical
 */
const applyShiftConstraint = (
  startX: number,
  startY: number,
  endX: number,
  endY: number
): { endX: number; endY: number } => {
  if (Math.abs(endX - startX) > Math.abs(endY - startY)) {
    return { endX, endY: startY };
  } else {
    return { endX: startX, endY };
  }
};

// Hover color - slightly lighter than selection
const HOVER_COLOR = "#42a5f5";

const handleDrawLineWithTicksAndDistance = (
  ctx: CanvasRenderingContext2D,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  distance: string,
  isConstrained: boolean,
  isSelected: boolean = false,
  isHovered: boolean = false,
  zoomLevel: number = 1,
  baseScale: number = 1
) => {
  // Adjust endX and endY to make the line straight if constrained
  if (isConstrained) {
    const constrained = applyShiftConstraint(startX, startY, endX, endY);
    endX = constrained.endX;
    endY = constrained.endY;
  }

  // Clamp zoom factor: max at 1 (no growth), min at 0.5 (shrinks when zoomed out)
  // Then apply baseScale for context-specific sizing (e.g., 0.75 for ramps)
  const clampedZoom = Math.max(0.5, Math.min(1, zoomLevel)) * baseScale;

  // Use selection/hover color
  const lineColor = isSelected ? SELECTED_COLOR : isHovered ? HOVER_COLOR : DEFAULT_COLOR;
  const baseLineWidth = isSelected ? 3 : isHovered ? 2.5 : 2;
  const lineWidth = baseLineWidth * clampedZoom;

  // Draw the main line
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  ctx.moveTo(startX, startY);
  ctx.lineTo(endX, endY);
  ctx.stroke();

  // Calculate the angle of the line
  const dx = endX - startX;
  const dy = endY - startY;
  const length = Math.sqrt(dx * dx + dy * dy);
  const unitX = dx / length;
  const unitY = dy / length;

  // Perpendicular vector
  const perpX = -unitY;
  const perpY = unitX;

  // Length of the tick (scaled with zoom)
  const tickLength = 10 * clampedZoom;

  // Draw tick at the start of the line
  ctx.beginPath();
  ctx.moveTo(startX + perpX * tickLength, startY + perpY * tickLength);
  ctx.lineTo(startX - perpX * tickLength, startY - perpY * tickLength);
  ctx.stroke();

  // Draw tick at the end of the line
  ctx.beginPath();
  ctx.moveTo(endX + perpX * tickLength, endY + perpY * tickLength);
  ctx.lineTo(endX - perpX * tickLength, endY - perpY * tickLength);
  ctx.stroke();

  // Calculate the midpoint of the line for the distance label
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;

  // Set font and styles for the distance box (scaled with zoom)
  const fontSize = 16 * clampedZoom;
  ctx.font = `${fontSize}px Lexend Deca`;
  ctx.fillStyle = isSelected ? SELECTED_COLOR : isHovered ? HOVER_COLOR : "white";
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = (isSelected || isHovered ? 2 : 1) * clampedZoom;

  // Calculate text width and height (scaled with zoom)
  const textWidth = ctx.measureText(distance).width;
  const padding = 5 * clampedZoom;
  const boxWidth = textWidth + padding * 2;
  const boxHeight = fontSize + padding;

  // Determine if the label should be inline or off to the side
  if (length > boxHeight * 2) {
    // Inline positioning
    const boxX = midX - boxWidth / 2;
    const boxY = midY - boxHeight / 2;

    // Draw a rounded rectangle for the distance box (inline)
    const radius = 5 * clampedZoom;
    ctx.fillStyle = isSelected ? "#e3f2fd" : isHovered ? "#e3f2fd" : "white"; // Light blue when selected/hovered
    ctx.beginPath();
    ctx.moveTo(boxX + radius, boxY);
    ctx.lineTo(boxX + boxWidth - radius, boxY);
    ctx.quadraticCurveTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + radius);
    ctx.lineTo(boxX + boxWidth, boxY + boxHeight - radius);
    ctx.quadraticCurveTo(
      boxX + boxWidth,
      boxY + boxHeight,
      boxX + boxWidth - radius,
      boxY + boxHeight
    );
    ctx.lineTo(boxX + radius, boxY + boxHeight);
    ctx.quadraticCurveTo(
      boxX,
      boxY + boxHeight,
      boxX,
      boxY + boxHeight - radius
    );
    ctx.lineTo(boxX, boxY + radius);
    ctx.quadraticCurveTo(boxX, boxY, boxX + radius, boxY);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Draw the distance text inside the box (inline)
    ctx.fillStyle = isSelected ? SELECTED_COLOR : isHovered ? HOVER_COLOR : DEFAULT_COLOR;
    ctx.textBaseline = "middle";
    ctx.fillText(distance, boxX + padding, boxY + boxHeight / 2 + 1 * clampedZoom);

    // Show delete hint when selected
    if (isSelected) {
      const hintText = "Backspace to delete";
      const hintFontSize = 11 * clampedZoom;
      ctx.font = `${hintFontSize}px Lexend Deca`;
      const hintWidth = ctx.measureText(hintText).width;
      const hintPadding = 6 * clampedZoom;
      const hintBoxWidth = hintWidth + hintPadding * 2;
      const hintBoxHeight = hintFontSize + hintPadding;
      const hintBoxX = boxX + (boxWidth - hintBoxWidth) / 2;
      const hintBoxY = boxY + boxHeight + 4 * clampedZoom;
      const hintRadius = 3 * clampedZoom;

      // Draw hint background
      ctx.fillStyle = "white";
      ctx.strokeStyle = SELECTED_COLOR;
      ctx.lineWidth = 1 * clampedZoom;
      ctx.beginPath();
      ctx.moveTo(hintBoxX + hintRadius, hintBoxY);
      ctx.lineTo(hintBoxX + hintBoxWidth - hintRadius, hintBoxY);
      ctx.quadraticCurveTo(hintBoxX + hintBoxWidth, hintBoxY, hintBoxX + hintBoxWidth, hintBoxY + hintRadius);
      ctx.lineTo(hintBoxX + hintBoxWidth, hintBoxY + hintBoxHeight - hintRadius);
      ctx.quadraticCurveTo(hintBoxX + hintBoxWidth, hintBoxY + hintBoxHeight, hintBoxX + hintBoxWidth - hintRadius, hintBoxY + hintBoxHeight);
      ctx.lineTo(hintBoxX + hintRadius, hintBoxY + hintBoxHeight);
      ctx.quadraticCurveTo(hintBoxX, hintBoxY + hintBoxHeight, hintBoxX, hintBoxY + hintBoxHeight - hintRadius);
      ctx.lineTo(hintBoxX, hintBoxY + hintRadius);
      ctx.quadraticCurveTo(hintBoxX, hintBoxY, hintBoxX + hintRadius, hintBoxY);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // Draw hint text
      ctx.fillStyle = SELECTED_COLOR;
      ctx.fillText(hintText, hintBoxX + hintPadding, hintBoxY + hintFontSize + hintPadding / 2 - 2 * clampedZoom);
    }
  } else {
    // Off to the side positioning
    const offset = Math.max(50 * clampedZoom, length / 3); // Adjust offset for small distances (scaled)

    // Calculate the box's top-left corner with dynamic offset
    const boxX = midX + perpX * offset - boxWidth / 2;
    const boxY = midY + perpY * offset - boxHeight / 2;

    // Draw a rounded rectangle for the distance box (off to the side)
    const radius = 5 * clampedZoom;
    ctx.fillStyle = isSelected ? "#e3f2fd" : isHovered ? "#e3f2fd" : "white"; // Light blue when selected/hovered
    ctx.beginPath();
    ctx.moveTo(boxX + radius, boxY);
    ctx.lineTo(boxX + boxWidth - radius, boxY);
    ctx.quadraticCurveTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + radius);
    ctx.lineTo(boxX + boxWidth, boxY + boxHeight - radius);
    ctx.quadraticCurveTo(
      boxX + boxWidth,
      boxY + boxHeight,
      boxX + boxWidth - radius,
      boxY + boxHeight
    );
    ctx.lineTo(boxX + radius, boxY + boxHeight);
    ctx.quadraticCurveTo(
      boxX,
      boxY + boxHeight,
      boxX,
      boxY + boxHeight - radius
    );
    ctx.lineTo(boxX, boxY + radius);
    ctx.quadraticCurveTo(boxX, boxY, boxX + radius, boxY);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Draw the distance text inside the box (off to the side)
    ctx.fillStyle = isSelected ? SELECTED_COLOR : isHovered ? HOVER_COLOR : DEFAULT_COLOR;
    ctx.textBaseline = "middle";
    ctx.fillText(distance, boxX + padding, boxY + boxHeight / 2 + 1 * clampedZoom);

    // Show delete hint when selected
    if (isSelected) {
      const hintText = "Backspace to delete";
      const hintFontSize = 11 * clampedZoom;
      ctx.font = `${hintFontSize}px Lexend Deca`;
      const hintWidth = ctx.measureText(hintText).width;
      const hintPadding = 6 * clampedZoom;
      const hintBoxWidth = hintWidth + hintPadding * 2;
      const hintBoxHeight = hintFontSize + hintPadding;
      const hintBoxX = boxX + (boxWidth - hintBoxWidth) / 2;
      const hintBoxY = boxY + boxHeight + 4 * clampedZoom;
      const hintRadius = 3 * clampedZoom;

      // Draw hint background
      ctx.fillStyle = "white";
      ctx.strokeStyle = SELECTED_COLOR;
      ctx.lineWidth = 1 * clampedZoom;
      ctx.beginPath();
      ctx.moveTo(hintBoxX + hintRadius, hintBoxY);
      ctx.lineTo(hintBoxX + hintBoxWidth - hintRadius, hintBoxY);
      ctx.quadraticCurveTo(hintBoxX + hintBoxWidth, hintBoxY, hintBoxX + hintBoxWidth, hintBoxY + hintRadius);
      ctx.lineTo(hintBoxX + hintBoxWidth, hintBoxY + hintBoxHeight - hintRadius);
      ctx.quadraticCurveTo(hintBoxX + hintBoxWidth, hintBoxY + hintBoxHeight, hintBoxX + hintBoxWidth - hintRadius, hintBoxY + hintBoxHeight);
      ctx.lineTo(hintBoxX + hintRadius, hintBoxY + hintBoxHeight);
      ctx.quadraticCurveTo(hintBoxX, hintBoxY + hintBoxHeight, hintBoxX, hintBoxY + hintBoxHeight - hintRadius);
      ctx.lineTo(hintBoxX, hintBoxY + hintRadius);
      ctx.quadraticCurveTo(hintBoxX, hintBoxY, hintBoxX + hintRadius, hintBoxY);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // Draw hint text
      ctx.fillStyle = SELECTED_COLOR;
      ctx.fillText(hintText, hintBoxX + hintPadding, hintBoxY + hintFontSize + hintPadding / 2 - 2 * clampedZoom);
    }
  }
};

// Minimum distance in pixels to be considered a valid measurement (filter accidental clicks)
const MIN_MEASUREMENT_LENGTH = 5;

type Props = {
  children?: React.ReactNode;
  pixelsToFeetRatio: number;
  overlayExpansionRatio?: number;
  zoomLevel?: number;
  baseScale?: number;
};

export const MeasuringTool: React.FC<Props> = ({
  children,
  pixelsToFeetRatio,
  overlayExpansionRatio = 1.25,
  zoomLevel = 1,
  baseScale = 1,
}) => {
  const { activeFBO } = useActiveFBOs();
  const {
    active: measuringToolActive,
    setActive: setMeasuringToolActive,
    measurements,
    addMeasurement,
    removeMeasurement,
    selectedMeasurementId,
    setSelectedMeasurementId,
  } = useMeasuringToolState();

  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  // Local state for in-progress measurement
  const [startPoint, setStartPoint] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [endPoint, setEndPoint] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isDrawing, setIsDrawing] = React.useState(false);
  const [isShiftPressed] = useKeyPress("Shift");

  // Hover state for visual feedback
  const [hoveredMeasurementId, setHoveredMeasurementId] = React.useState<string | null>(null);

  // Calculate distance string for a measurement stored in feet
  const calculateDistanceLabelFromFeet = React.useCallback(
    (startFeet: { x: number; y: number }, endFeet: { x: number; y: number }, isConstrained: boolean) => {
      let finalEnd = endFeet;
      if (isConstrained) {
        const constrained = applyShiftConstraint(startFeet.x, startFeet.y, endFeet.x, endFeet.y);
        finalEnd = { x: constrained.endX, y: constrained.endY };
      }

      // Distance is already in feet since coordinates are in feet
      const feetDistance = Math.sqrt(
        Math.pow(finalEnd.x - startFeet.x, 2) + Math.pow(finalEnd.y - startFeet.y, 2)
      );

      return activeFBO?.settings?.useMetricSystem
        ? `${formatNumber(feetToMeters(feetDistance), 2)} m`
        : `${formatNumber(feetDistance, 1)} ft`;
    },
    [activeFBO?.settings?.useMetricSystem]
  );

  // Calculate distance string for in-progress measurement (still in pixels)
  const calculateDistanceLabelFromPixels = React.useCallback(
    (startPixels: { x: number; y: number }, endPixels: { x: number; y: number }, isConstrained: boolean) => {
      let finalEnd = endPixels;
      if (isConstrained) {
        const constrained = applyShiftConstraint(startPixels.x, startPixels.y, endPixels.x, endPixels.y);
        finalEnd = { x: constrained.endX, y: constrained.endY };
      }

      const pixelDistance = Math.sqrt(
        Math.pow(finalEnd.x - startPixels.x, 2) + Math.pow(finalEnd.y - startPixels.y, 2)
      );

      // Guard against division by zero
      if (pixelsToFeetRatio <= 0) return "0 ft";

      const feetDistance = pixelDistance / pixelsToFeetRatio;

      return activeFBO?.settings?.useMetricSystem
        ? `${formatNumber(feetToMeters(feetDistance), 2)} m`
        : `${formatNumber(feetDistance, 1)} ft`;
    },
    [activeFBO?.settings?.useMetricSystem, pixelsToFeetRatio]
  );

  // Reset in-progress measurement when tool is deactivated
  React.useEffect(() => {
    if (!measuringToolActive) {
      setStartPoint(null);
      setEndPoint(null);
      setIsDrawing(false);
    }
  }, [measuringToolActive]);

  // Keyboard shortcuts
  // Delete selected measurement
  useHotkeys(
    "delete,backspace",
    () => {
      if (selectedMeasurementId && measuringToolActive) {
        removeMeasurement(selectedMeasurementId);
      }
    },
    { enabled: measuringToolActive && !!selectedMeasurementId },
    [selectedMeasurementId, removeMeasurement, measuringToolActive]
  );

  // Escape to deselect and cancel in-progress measurement
  useHotkeys(
    "escape",
    () => {
      if (measuringToolActive) {
        setSelectedMeasurementId(null);
        setStartPoint(null);
        setEndPoint(null);
        setIsDrawing(false);
      }
    },
    { enabled: measuringToolActive },
    [measuringToolActive, setSelectedMeasurementId]
  );

  // Shift+M key to toggle measuring tool
  useHotkeys(
    "shift+m",
    () => {
      setMeasuringToolActive(!measuringToolActive);
    },
    [measuringToolActive, setMeasuringToolActive]
  );

  const handleCanvasClick = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!measuringToolActive || !canvasRef.current) {
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // If not currently drawing, check if clicking on existing measurement
    if (!isDrawing) {
      const hitMeasurementId = hitTestMeasurement(x, y, measurements, pixelsToFeetRatio, zoomLevel, baseScale);

      if (hitMeasurementId) {
        // Clicked on existing measurement - select/deselect it
        if (selectedMeasurementId === hitMeasurementId) {
          setSelectedMeasurementId(null);
        } else {
          setSelectedMeasurementId(hitMeasurementId);
        }
        return;
      }

      // Not clicking on measurement - start new measurement
      setSelectedMeasurementId(null);
      setStartPoint({ x, y });
      setEndPoint({ x, y });
      setIsDrawing(true);
      return;
    }

    // Currently drawing - finish the measurement
    if (startPoint && endPoint) {
      // Apply shift constraint if needed (in pixel space)
      let finalEndPixels = endPoint;
      if (isShiftPressed) {
        const constrained = applyShiftConstraint(
          startPoint.x,
          startPoint.y,
          endPoint.x,
          endPoint.y
        );
        finalEndPixels = { x: constrained.endX, y: constrained.endY };
      }

      // Calculate pixel distance
      const pixelDistance = Math.sqrt(
        Math.pow(finalEndPixels.x - startPoint.x, 2) +
          Math.pow(finalEndPixels.y - startPoint.y, 2)
      );

      // Only save if measurement is long enough (filter accidental clicks)
      if (pixelDistance >= MIN_MEASUREMENT_LENGTH && pixelsToFeetRatio > 0) {
        // Convert pixel coordinates to feet for storage
        const startFeet = {
          x: startPoint.x / pixelsToFeetRatio,
          y: startPoint.y / pixelsToFeetRatio,
        };
        const endFeet = {
          x: finalEndPixels.x / pixelsToFeetRatio,
          y: finalEndPixels.y / pixelsToFeetRatio,
        };

        addMeasurement({
          startPointFeet: startFeet,
          endPointFeet: endFeet,
          isConstrained: isShiftPressed,
        });
      }

      // Reset drawing state
      setStartPoint(null);
      setEndPoint(null);
      setIsDrawing(false);
    }
  };

  // Draw all measurements (called on each frame)
  const drawAllMeasurements = React.useCallback(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // Guard against invalid ratio
    if (pixelsToFeetRatio <= 0) return;

    // Draw all completed measurements from store (stored in feet, render in pixels)
    measurements.forEach((measurement) => {
      const isSelected = measurement.id === selectedMeasurementId;
      const isHovered = measurement.id === hoveredMeasurementId && !isSelected;
      const distanceLabel = calculateDistanceLabelFromFeet(
        measurement.startPointFeet,
        measurement.endPointFeet,
        measurement.isConstrained
      );

      // Convert feet back to current pixel coordinates
      const startPixels = {
        x: measurement.startPointFeet.x * pixelsToFeetRatio,
        y: measurement.startPointFeet.y * pixelsToFeetRatio,
      };
      const endPixels = {
        x: measurement.endPointFeet.x * pixelsToFeetRatio,
        y: measurement.endPointFeet.y * pixelsToFeetRatio,
      };

      handleDrawLineWithTicksAndDistance(
        ctx,
        startPixels.x,
        startPixels.y,
        endPixels.x,
        endPixels.y,
        distanceLabel,
        measurement.isConstrained,
        isSelected,
        isHovered,
        zoomLevel,
        baseScale
      );
    });

    // Draw in-progress measurement last (on top) - still in pixels
    if (isDrawing && startPoint && endPoint) {
      const distanceLabel = calculateDistanceLabelFromPixels(startPoint, endPoint, isShiftPressed);
      handleDrawLineWithTicksAndDistance(
        ctx,
        startPoint.x,
        startPoint.y,
        endPoint.x,
        endPoint.y,
        distanceLabel,
        isShiftPressed,
        false,
        false,
        zoomLevel,
        baseScale
      );
    }
  }, [
    measurements,
    selectedMeasurementId,
    hoveredMeasurementId,
    isDrawing,
    startPoint,
    endPoint,
    isShiftPressed,
    calculateDistanceLabelFromFeet,
    calculateDistanceLabelFromPixels,
    pixelsToFeetRatio,
    zoomLevel,
    baseScale,
  ]);

  // Redraw when measurements or selection changes
  React.useEffect(() => {
    if (measuringToolActive) {
      drawAllMeasurements();
    } else {
      // Clear the canvas when tool is deactivated
      const ctx = canvasRef.current?.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      }
    }
  }, [measuringToolActive, drawAllMeasurements]);

  const draw = React.useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      drawAllMeasurements();
    },
    [drawAllMeasurements]
  );

  return (
    <ClearCanvasOverlay
      active={measuringToolActive}
      ref={canvasRef}
      expandBy={overlayExpansionRatio}
      onClick={handleCanvasClick}
      onDraw={draw}
      onResize={() => {
        // Redraw measurements after canvas dimensions change
        // (canvas content is cleared when dimensions are set)
        if (measuringToolActive) {
          drawAllMeasurements();
        }
      }}
      onMouseMove={(e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!canvasRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        if (isDrawing && startPoint) {
          // Update in-progress measurement
          setEndPoint({ x, y });
          drawAllMeasurements();
        } else {
          // Check for hover on existing measurements
          const hitId = hitTestMeasurement(x, y, measurements, pixelsToFeetRatio, zoomLevel, baseScale);
          if (hitId !== hoveredMeasurementId) {
            setHoveredMeasurementId(hitId);
          }
        }
      }}
      style={{
        cursor: measuringToolActive
          ? hoveredMeasurementId
            ? "pointer"
            : "crosshair"
          : "default",
      }}
    >
      {children}
    </ClearCanvasOverlay>
  );
};
